import { useT } from '../../hooks/useTranslation';
import { srcSetFor } from '../../utils/images';
import { useScrollDriven, clamp01, ramp } from '../../hooks/useScrollDriven';
import { useInViewOnce } from '../../hooks/useInViewOnce';

/**
 * Parallax (framer nélkül): p = a szekció útja a viewporton át (0 = a teteje alul belép, 1 = az alja
 * felül kilép). Szöveg: y +100 → -100 px, opacity 0 → 1 a 10–40 % között; kép: y -50 → +50 px.
 */
const aboutVars = (_scrollY: number, vh: number, el: HTMLElement) => {
  const top = el.getBoundingClientRect().top;
  const p = clamp01((vh - top) / (vh + el.offsetHeight));
  return {
    '--about-text-y': `${(100 - 200 * p).toFixed(1)}px`,
    '--about-text-o': ramp(p, 0.1, 0.4).toFixed(3),
    '--about-img-y': `${(-50 + 100 * p).toFixed(1)}px`,
  };
};

export const About = () => {
  const t = useT();
  const sectionRef = useScrollDriven<HTMLElement>(aboutVars);
  const [revealRef, revealed] = useInViewOnce<HTMLDivElement>('-180px');

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative py-16 md:py-24 overflow-hidden flex items-center"
      style={{ background: 'radial-gradient(ellipse at 60% 50%, #2d4a35 0%, #1a2d20 100%)' }}
    >
      {/* Grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '128px 128px',
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(244,241,237,0.03)_1px,transparent_1px)] bg-size-[24px_24px] pointer-events-none" />

      <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-12 relative flex flex-col md:flex-row items-center justify-between">

        <div
          style={{ transform: 'translateY(var(--about-text-y, 0px))', opacity: 'var(--about-text-o, 1)' }}
          className="w-full md:w-5/12 z-20 relative pt-20 md:pt-0"
        >
          <span className="text-gold uppercase tracking-[0.3em] pl-1 text-xs font-semibold mb-8 block relative">
            <span className="absolute -left-6 top-1/2 w-4 h-px bg-gold" />
            {t.about.label}
          </span>

          <h2 className="text-5xl md:text-7xl lg:text-8xl font-serif text-sand mb-12 leading-[0.9] tracking-tighter">
            <span className="block italic font-light">{t.about.heading1}</span>
            <span className="block ml-12">{t.about.heading2}</span>
          </h2>

          <div className="space-y-8 text-sand/80 font-light leading-relaxed text-lg max-w-md ml-auto md:ml-12 border-l border-sand/20 pl-8">
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>

            <a href="#gallery" className="inline-flex items-center text-sand uppercase tracking-widest text-sm font-medium hover:text-gold transition-colors mt-8 group">
              {t.about.cta}
              <span className="ml-4 w-12 h-px bg-sand group-hover:w-24 group-hover:bg-gold transition-all duration-500 ease-out" />
            </a>
          </div>
        </div>

        <div
          style={{ transform: 'translateY(var(--about-img-y, 0px))' }}
          className="w-full md:w-6/12 mt-24 md:mt-0 relative z-10"
        >
          <div ref={revealRef} className="relative w-full aspect-3/4 overflow-hidden -rotate-2 group">
            <div className="absolute inset-0 z-10" style={{ clipPath: 'polygon(10% 0, 100% 5%, 90% 100%, 0% 95%)' }}>
              <img
                src="/images/ahaaziko-0521-203953.avif"
                srcSet={srcSetFor('ahaaziko-0521-203953')}
                sizes="(max-width: 768px) 100vw, (max-width: 1400px) 50vw, 700px"
                alt="Haaziko alpesi faház — természetközeli megjelenés"
                width="600"
                height="800"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover scale-110 group-hover:scale-100 transition-transform duration-[2s] ease-out brightness-90"
              />
            </div>

            {/* Függöny-leleplezés: a sötét fedőréteg alulról felhúzódik, amikor a kép a viewportba ér */}
            <div
              className={`absolute inset-0 z-20 bg-forest origin-bottom transition-transform duration-[1300ms] delay-150 ease-[cubic-bezier(0.16,1,0.3,1)] ${revealed ? 'scale-y-0' : 'scale-y-100'}`}
            />

            <div className="absolute top-10 -right-10 w-full h-full border border-gold/30 rotate-3 z-0" style={{ clipPath: 'polygon(10% 0, 100% 5%, 90% 100%, 0% 95%)' }} />
          </div>
        </div>

      </div>

      <h3 className="absolute right-[-10vw] top-[30vh] text-[20vw] font-serif text-sand/3 select-none pointer-events-none rotate-90 origin-right whitespace-nowrap">
        H A A Z I K O
      </h3>

    </section>
  );
};
